import { useLayoutEffect, useRef } from "react";
import { OverlayScrollbars } from "overlayscrollbars";
import { resizeGestureActive } from "../lib/settledResize.js";

const baseOverlayOptions = {
  scrollbars: {
    autoHide: "leave",
    autoHideDelay: 160,
    theme: "os-theme-origin",
  },
};

const EMPTY_OPTIONS = Object.freeze({});

function mergeOverlayOptions(options = EMPTY_OPTIONS) {
  return {
    ...baseOverlayOptions,
    ...options,
    scrollbars: {
      ...baseOverlayOptions.scrollbars,
      ...(options.scrollbars || {}),
    },
    overflow: {
      ...(options.overflow || {}),
    },
    update: {
      ...(options.update || {}),
    },
  };
}

function assignRef(targetRef, value) {
  if (!targetRef) return;
  if (typeof targetRef === "function") {
    targetRef(value);
    return;
  }
  targetRef.current = value;
}

// Every instance sleeps while a divider is dragged (a resize gesture class
// on <body>, lib/settledResize.js) and wakes to one update when it is let
// go. Awake, each one whose host a drag resizes reads its computed styles
// at every frame: traces of the workspace dock's seam showed those forced
// reflows at about a third of the drag's main thread.
const sleepers = new Set();
let gestureWatch = null;
let gestureUp = false;
function watchGestures() {
  if (gestureWatch || typeof MutationObserver === "undefined" || !document.body) return;
  gestureUp = resizeGestureActive();
  gestureWatch = new MutationObserver(() => {
    const up = resizeGestureActive();
    if (up === gestureUp) return;
    gestureUp = up;
    for (const instance of sleepers) {
      instance.sleep(up);
      if (!up) instance.update();
    }
  });
  gestureWatch.observe(document.body, { attributes: true, attributeFilter: ["class"] });
}

export default function ScrollArea({
  disabled = false,
  children,
  className = "",
  contentClassName = "",
  hostRef: externalHostRef = null,
  instanceRef: externalInstanceRef = null,
  options = EMPTY_OPTIONS,
  observeMutations = true,
  maxHeight,
  "aria-busy": ariaBusy,
  "data-content-scroll": dataContentScroll,
  "data-context-scroll": dataContextScroll,
  "data-sidebar-scroll": dataSidebarScroll,
  "data-export-full-width": dataExportFullWidth,
}) {
  const hostRef = useRef(null);
  const contentRef = useRef(null);
  const instanceRef = useRef(null);

  useLayoutEffect(() => {
    if (disabled || !hostRef.current) return undefined;

    const instance = OverlayScrollbars(hostRef.current, mergeOverlayOptions(options));
    instanceRef.current = instance;
    assignRef(externalInstanceRef, instance);
    watchGestures();
    sleepers.add(instance);
    if (gestureUp) instance.sleep(true);
    const contentNode = contentRef.current;
    // CPU profiles showed OverlayScrollbars dominating the trace
    // (~550ms in overlayscrollbars.mjs) because every React-driven
    // style/class change inside the scroll content fired a forced
    // update. Two changes here:
    //  - drop `attributes: true` from the MutationObserver: hover
    //    backgrounds, active-row highlights, cursor toggles, etc.
    //    don't change scroll geometry — ResizeObserver already
    //    catches real size changes via the host/content nodes.
    //  - call `instance.update()` (not `update(true)`) so the
    //    library can short-circuit no-op updates via its own
    //    change-detection rather than re-applying everything.
    //  - coalesce to one update() per frame: streaming chat content
    //    fires the MutationObserver many times per frame (one per
    //    token via characterData/childList). Without this guard each
    //    callback queues its own rAF, so N mutations in a frame run N
    //    update() calls in the next — each doing getComputedStyle +
    //    setAttribute passes. CPU profiles showed this as a ~140ms
    //    long task during streaming. The pending-handle guard collapses
    //    the burst into a single update().
    //  - hold the update while a divider is dragged (a resize gesture
    //    class on <body>, lib/settledResize.js): every frame of a drag
    //    resizes the content, and the one update once it is let go
    //    catches up.
    let rafHandle = 0;
    const scheduleUpdate = () => {
      if (rafHandle) return;
      const tick = () => {
        if (resizeGestureActive()) {
          rafHandle = window.requestAnimationFrame(tick);
          return;
        }
        rafHandle = 0;
        instance.update();
      };
      rafHandle = window.requestAnimationFrame(tick);
    };

    const resizeObserver =
      contentNode && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            scheduleUpdate();
          })
        : null;
    const mutationObserver =
      observeMutations && contentNode && typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => {
            scheduleUpdate();
          })
        : null;

    resizeObserver?.observe(contentNode);
    mutationObserver?.observe(contentNode, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    scheduleUpdate();

    return () => {
      if (rafHandle) window.cancelAnimationFrame(rafHandle);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      sleepers.delete(instance);
      instanceRef.current = null;
      assignRef(externalInstanceRef, null);
      instance.destroy();
    };
  }, [disabled]);

  useLayoutEffect(() => {
    if (disabled || !instanceRef.current) return;
    instanceRef.current.options(mergeOverlayOptions(options));
    // Options actually changed — keep the force here, this only
    // fires on real option-prop changes (not on every render).
    instanceRef.current.update(true);
  }, [disabled, options]);

  const setHostRef = (node) => {
    hostRef.current = node;
    assignRef(externalHostRef, node);
  };

  if (disabled) {
    return (
      <div
        ref={setHostRef}
        className={className}
        style={{ maxHeight }}
        aria-busy={ariaBusy}
        data-content-scroll={dataContentScroll}
        data-context-scroll={dataContextScroll}
        data-sidebar-scroll={dataSidebarScroll}
        data-export-full-width={dataExportFullWidth}
      >
        <div ref={contentRef} className={contentClassName}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setHostRef}
      data-overlayscrollbars-initialize
      className={className}
      style={{ maxHeight }}
      aria-busy={ariaBusy}
      data-content-scroll={dataContentScroll}
      data-context-scroll={dataContextScroll}
      data-sidebar-scroll={dataSidebarScroll}
      data-export-full-width={dataExportFullWidth}
    >
      <div ref={contentRef} data-scroll-content className={contentClassName}>
        {children}
      </div>
    </div>
  );
}
